import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, User, Loader2, Tag } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import SEO, { breadcrumbSchema } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      if (res.ok) {
        setPost(await res.json());
      } else {
        setNotFound(true);
      }
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Markdown to HTML with internal link support
  const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
      .replace(/### (.*?)$/gm, '<h3 class="text-lg font-semibold text-[hsl(var(--foreground))] mt-6 mb-3">$1</h3>')
      .replace(/## (.*?)$/gm, '<h2 class="text-xl font-bold text-[hsl(var(--foreground))] mt-8 mb-4">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[hsl(var(--primary))] hover:underline font-medium">$1</a>')
      .replace(/^- (.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
      .replace(/(<li.*?<\/li>\n?)+/g, '<ul class="list-disc list-inside my-4 space-y-1">$&</ul>')
      .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed">')
      .replace(/\n/g, '<br/>');
    return `<p class="mb-4 leading-relaxed">${html}</p>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Beitrag nicht gefunden</h1>
          <Link to="/blog" className="text-[hsl(var(--primary))] hover:underline">Zurück zum Blog</Link>
        </div>
      </div>
    );
  }

  const keywords = post.meta_keywords ? post.meta_keywords.split(',').map(k => k.trim()).filter(Boolean) : [];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title={post.title}
        description={post.meta_description || post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        type="article"
        image={post.featured_image}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': post.title,
          'description': post.meta_description || post.excerpt,
          'image': post.featured_image || '',
          'datePublished': post.published_at,
          'dateModified': post.updated_at,
          'author': {
            '@type': 'Organization',
            'name': post.author_info?.name || 'ZO Werbefläche',
            'description': post.author_info?.description || ''
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'ZO Werbefläche'
          },
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': `${window.location.origin}/blog/${post.slug}`
          },
          'keywords': post.meta_keywords
        }}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors mb-8"
          data-testid="blog-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück zum Blog
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <header className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-4 leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[hsl(var(--muted-foreground))]">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.published_at ? new Date(post.published_at).toLocaleDateString('de-CH', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Entwurf'}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                ZO Werbefläche
              </span>
              {post.ai_generated && (
                <Badge variant="outline">KI-generiert</Badge>
              )}
            </div>
          </header>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={post.featured_image.startsWith('/') ? `${API_URL}${post.featured_image}` : post.featured_image}
                alt={post.image_alt || post.title}
                className="w-full h-auto max-h-[400px] object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-lg max-w-none text-[hsl(var(--foreground))]"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }}
            data-testid="blog-post-content"
          />

          {/* Author E-E-A-T Section */}
          {post.author_info && (
            <div className="mt-10 p-5 bg-[hsl(var(--accent)/0.4)] border border-[hsl(var(--border))] rounded-xl flex items-start gap-4" data-testid="blog-author-section">
              <div className="w-12 h-12 bg-[hsl(var(--primary)/0.1)] rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-[hsl(var(--primary))]" />
              </div>
              <div>
                <p className="font-semibold text-[hsl(var(--foreground))]">{post.author_info.name}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{post.author_info.description}</p>
              </div>
            </div>
          )}

          {/* Keywords */}
          {keywords.length > 0 && (
            <div className="mt-10 pt-6 border-t border-[hsl(var(--border))]">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                {keywords.map((kw, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{kw}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 p-6 bg-[hsl(var(--primary)/0.05)] border border-[hsl(var(--primary)/0.2)] rounded-xl text-center">
            <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-2">
              Werbefläche im Zürcher Oberland buchen?
            </h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              Finden Sie den idealen Standort für Ihre mobile Werbung.
            </p>
            <Link to="/locations">
              <button className="bg-[hsl(var(--primary))] text-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition-opacity" data-testid="blog-cta-button">
                Standorte ansehen
              </button>
            </Link>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default BlogPostPage;
